import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';

import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { Error } from '../../../../models/error.model';
import { Message } from '../../../../models/message.model';
import { UseOutletContextStorageCenter } from './StorageCenter';
import { CompanyStorageCenters } from '../../../../models/company.storage.centers.model';
import { StorageCenter } from '../../../../models/storage.center.model';
import { StorageCenterDA } from '../../../../services/admin/storage.center.service';
import { GlobalSwal } from '../../../../styles/swal.style';
import { modalShow } from '../../../../utils/modal.util';
import { getError, getMessage } from '../../../../languages/translations/response';
import { sortColumn } from '../../../../scripts/table.script';
import { formatAddress } from '../../../../scripts/format.value.script';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';

import AppModalStorageCenterCreate from '../../../../components/modal/ModalStorageCenterCreate';
import AppModalStorageCenterUpdate from '../../../../components/modal/ModalStorageCenterUpdate';
import AppModalStorageTransfer from '../../../../components/modal/ModalStorageTransfer';
import AppPagination from '../../../../components/element/Pagination';
import AppPreloader from '../../../../components/Preloader';
import AppSortTable from '../../../../components/element/SortTable';
import AppInventoryListHeader from '../component/InventoryListHeader';

export interface StorageCenterListPageProps {};

let errorResponse: Error, messageResponse: Message, storageCentersResponse: CompanyStorageCenters;

const StorageCenterListPage: React.FunctionComponent<StorageCenterListPageProps> = props => {
  const {setRoute, companyForUser, storageCentersForCompany, loadStorageCentersForCompany} = UseOutletContextStorageCenter()
  const {lang} = StateLanguage()

  const [mounted, setMounted] = useState(false)
  const [filter, setFilter] = useState({search: '', options: {sort_field: '', sort_mode: ''}})
  const [checked, setChecked] = useState({count: 0, list: [] as string[]})
  const [pagination, setPagination] = useState({current: 1, total: 0, pages: 1, limit: 20})
  const [storageCenter, setStorageCenter] = useState<StorageCenter | null>(null)
  const [storageCenters, setStorageCenters] = useState<StorageCenter[] | null>(null)

  const loadStorageCenters = async (id_company: string, callback: any, page: number = 1, limit: number = pagination.limit) => {
    await StorageCenterDA.getStorageCenters(id_company, filter.search, filter.options.sort_field, filter.options.sort_mode, page, limit).then( (response) => {
      if (response.status === 200) {
        storageCentersResponse = response.data?.data ?? response.data
        const storageItems = storageCentersResponse.items ?? storageCentersResponse.storage_centers ?? []
        const pg = storageCentersResponse.pagination ?? { page, limit: pagination.limit, total: storageItems.length, pages: 1 }

        setStorageCenters(storageItems)
        setChecked({count: 0, list: []})
        setPagination({current: pg.page ?? page, total: pg.total ?? storageItems.length, pages: pg.pages ?? 1, limit: pg.limit ?? pagination.limit})
        callback()
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const handleChangePage = (page: number, newLimit?: number) => {
    if (companyForUser) loadStorageCenters(companyForUser.id, () => {}, page, newLimit ?? pagination.limit)
  }

  const submitStorageCenterUpdate = async (item: StorageCenter) => {
    setStorageCenter(item)
    modalShow('modal-storage-center-update')
  }

  const submitStorageCenterDelete = async (item: string) => {
    if (companyForUser) {
      GlobalSwal.fire({
        title: lang.labels.youWantToDeleteTheStorageCenter,
        text: lang.labels.youWillNotBeAbleToUndo,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: lang.labels.yesDelete,
        cancelButtonText: lang.labels.noCancel
      }).then(async (result) => {
        if (result.isConfirmed) {
          await StorageCenterDA.deleteStorageCenters(companyForUser.id, [item]).then( (response) => {
            if (response.status === 200) {
              messageResponse = response.data

              loadStorageCenters(companyForUser.id, () => {
                loadStorageCentersForCompany(companyForUser.id)

                GlobalSwal.fire({
                  title: getMessage(messageResponse.message, lang.code),
                  text: lang.labels.actionCompletedReturningToPage,
                  icon: 'success',
                  showConfirmButton: false,
                  timer: 2200
                })
              })
            } else {
              errorResponse = response.data

              GlobalSwal.fire({
                title: getError(errorResponse.code, lang.code, errorResponse.message),
                text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
                icon: 'error',
                confirmButtonText: lang.labels.okGotIt
              })
            }
          }).catch( (error) => {
            errorWebHandler(lang, error)
          })
        }
      })
    }
  }

  const handleChangeSelectAllStorageCenters = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    let list: string[] = (event.target.checked) ? event.target.value.split(',') : []
    setChecked({...checked, count: list.length, list: list})
  }

  const handleChangeSelectStorageCenter = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    let list: string[] = (event.target.checked) ? (!checked.list.includes(event.target.value)) ? checked.list.concat(event.target.value) : checked.list : checked.list.filter((item) => (item !== event.target.value))
    setChecked({...checked, count: list.length, list: list})
  }

  const executeSortList = (item: string) => {
    if (companyForUser) {
      sortColumn(item, filter.options)
      setFilter({search: filter.search, options: {...filter.options}})
      loadStorageCenters(companyForUser.id, () => {})
    }
  }

  useEffect( () => {
    if (companyForUser) {
      filter.search = ''
      filter.options.sort_field = ''
      filter.options.sort_mode = ''
      setFilter({search: '', options: {sort_field: '', sort_mode: ''}})
      setChecked({count: 0, list: []})

      setStorageCenters(null)
      loadStorageCenters(companyForUser.id, () => {})
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.storageCenter, branch: lang.labels.listStorageCenter}, company: true})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { storageCenters
        ?
        <div className="card card-flush">
          <AppInventoryListHeader inventory={"storage"} filter={filter} setFilter={setFilter} checked={checked} loadInventory={loadStorageCenters}></AppInventoryListHeader>
          <div className="card-body pt-0">
            <div className="dataTables_wrapper">
              <div className="table-responsive">
                <table className="table align-middle table-row-dashed mb-2">
                  <thead className="border-bottom">
                    <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                      <th className="w-25px">
                        <div className="form-check form-check-sm form-check-custom form-check-solid">
                          <input className="form-check-input" type="checkbox" value={storageCenters.map(item => item.id)} checked={storageCenters.length === checked.count && checked.count > 0} onChange={handleChangeSelectAllStorageCenters} />
                        </div>
                      </th>
                      <th className="min-w-350px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("storage")}>
                          <span className="me-1">{lang.labels.storageCenter}</span>
                          <AppSortTable attribute={"storage"} field={filter.options.sort_field} mode={filter.options.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="w-125px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("quantity")}>
                          <span className="me-1">{lang.labels.quantity}</span>
                          <AppSortTable attribute={"quantity"} field={filter.options.sort_field} mode={filter.options.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="w-100px text-end">{lang.labels.actions}</th>
                    </tr>
                  </thead>
                  { storageCenters.length > 0
                    ?
                    <tbody className="text-secondary fw-bold">
                      { storageCenters.map (( (item, index) => { return (
                        <tr key={index} className="row-dashed">
                          <td>
                            <div className="form-check form-check-sm form-check-custom form-check-solid">
                              <input className="form-check-input" type="checkbox" value={item.id} checked={checked.list.includes(item.id)} onChange={handleChangeSelectStorageCenter} />
                            </div>
                          </td>
                          <td className="d-flex flex-column">
                            <Link to={"/main/inventory/storage/view/" + item.id} className="fw-bolder text-dark text-capitalize text-hover-primary mb-1">{item.name}</Link>
                            <small>{formatAddress(lang, item.address)}</small>
                          </td >
                          <td className={`text-center fw-bolder ${item.quantity > 0 ? "text-success" : "text-danger"}`}>{item.quantity}</td>
                          <td className="text-end">
                            <Dropdown>
                              <Dropdown.Toggle variant="btn btn-sm btn-light btn-active-light-primary">
                                <div className="d-flex align-items-center">
                                  {lang.labels.actions}
                                  <span className="svg-icon svg-icon-5 me-0">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                      <path d="M11.4343 12.7344L7.25 8.55005C6.83579 8.13583 6.16421 8.13584 5.75 8.55005C5.33579 8.96426 5.33579 9.63583 5.75 10.05L11.2929 15.5929C11.6834 15.9835 12.3166 15.9835 12.7071 15.5929L18.25 10.05C18.6642 9.63584 18.6642 8.96426 18.25 8.55005C17.8358 8.13584 17.1642 8.13584 16.75 8.55005L12.5657 12.7344C12.2533 13.0468 11.7467 13.0468 11.4343 12.7344Z" fill="black"></path>
                                    </svg>
                                  </span>
                                </div>
                              </Dropdown.Toggle>
                              <Dropdown.Menu renderOnMount popperConfig={{ strategy: "fixed" }} variant="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg-light-primary w-auto p-3 fs-7" align="end">
                                <Dropdown.Item as={"div"} bsPrefix="menu-item">
                                  <Link to={"/main/inventory/storage/operation/" + item.id} className="menu-link fw-bold px-3 py-1 m-0">{lang.labels.operations}</Link>
                                </Dropdown.Item>
                                <Dropdown.Item bsPrefix="menu-item" onClick={() => submitStorageCenterUpdate(item)}>
                                  <p className="menu-link fw-bold px-3 py-1 m-0">{lang.labels.edit}</p>
                                </Dropdown.Item>
                                <Dropdown.Item bsPrefix="menu-item" onClick={() => submitStorageCenterDelete(item.id)}>
                                  <p className="menu-link fw-bold px-3 py-1 m-0">{lang.labels.delete}</p>
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                      )}))}
                    </tbody>
                    :
                    <tbody>
                      <tr className="text-secondary fw-bold">
                        <td colSpan={4} className="text-center">{lang.labels.noRecordsFound}</td>
                      </tr>
                    </tbody>
                  }
                </table>
              </div>
              <AppPagination
                showOption={true}
                pagination={{
                  current: pagination.current,
                  total: pagination.total,
                  limit: pagination.limit,
                  pages: Array.from({ length: pagination.pages }, (_, i) => i + 1),
                }}
                loadPagination={handleChangePage}
              ></AppPagination>
            </div>
          </div>
        </div>
        :
        <AppPreloader></AppPreloader>
      }
      <AppModalStorageCenterCreate companyForUser={companyForUser} storageCentersForCompany={storageCentersForCompany} storageCenters={storageCenters} setStorageCenters={setStorageCenters} loadStorageCentersForCompany={loadStorageCentersForCompany}></AppModalStorageCenterCreate>
      <AppModalStorageCenterUpdate companyForUser={companyForUser} storageCentersForCompany={storageCentersForCompany} storageCenter={storageCenter} storageCenters={storageCenters} setStorageCenter={setStorageCenter} setStorageCenters={setStorageCenters} loadStorageCentersForCompany={loadStorageCentersForCompany}></AppModalStorageCenterUpdate>
      <AppModalStorageTransfer companyForUser={companyForUser} storageCentersForCompany={storageCenters ?? storageCentersForCompany ?? []} onSuccess={() => { if (companyForUser) { loadStorageCenters(companyForUser.id, () => { loadStorageCentersForCompany(companyForUser.id) }) } }}></AppModalStorageTransfer>
    </div>
  )
};/*  */

export default StorageCenterListPage;
